// src/admin/AdminPricingCalendar.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import allotmentService from '../services/allotmentService';
import currencyService from '../services/currencyService';
import calendarVersionService from '../services/calendarVersionService';

function AdminPricingCalendar() {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allowances, setAllowances] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDateData, setSelectedDateData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState(new Set());

  // Currency display state
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  const [availableCurrencies, setAvailableCurrencies] = useState(['USD']);
  // const [showBothCurrencies, setShowBothCurrencies] = useState(true);

  // Edit form state
  const [editForm, setEditForm] = useState({
    dailyAllowance: '',
    baseCurrency: 'USD',
    isLocked: false,
    reason: ''
  });

  // Version control state
  const [versions, setVersions] = useState([]);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showSaveVersionModal, setShowSaveVersionModal] = useState(false);
  const [versionForm, setVersionForm] = useState({
    versionName: '',
    notes: ''
  });
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Load routes on mount
  useEffect(() => {
    fetchRoutes();
    fetchCurrencies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load allowances when route or date changes
  useEffect(() => {
    if (selectedRoute) {
      fetchAllowances();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute, currentDate]);

  const fetchRoutes = async () => {
    try {
      const routesSnapshot = await getDocs(
        query(collection(db, 'routes'), orderBy('name'))
      );
      const routesData = routesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRoutes(routesData);
      
      if (routesData.length > 0 && !selectedRoute) {
        setSelectedRoute(routesData[0].id);
      }
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const currencies = await currencyService.getAvailableCurrencies();
      setAvailableCurrencies(currencies);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const fetchAllowances = async () => {
    try {
      setLoading(true);

      // Get start and end of current month
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      // Get last day of month
      const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      // Add one day since getAllowancesForDateRange excludes the end date (for pricing calculations)
      // but we need to include the last day for the calendar
      const end = new Date(lastDay);
      end.setDate(end.getDate() + 1);

      const startDate = start.toISOString().split('T')[0];
      const endDate = end.toISOString().split('T')[0];
      
      const allowanceData = await allotmentService.getAllowancesForDateRange(
        selectedRoute, 
        startDate, 
        endDate
      );
      
      // Convert array to object for easy lookup
      const allowanceMap = {};
      allowanceData.forEach(item => {
        allowanceMap[item.date] = item;
      });
      
      setAllowances(allowanceMap);
    } catch (error) {
      console.error('Error fetching allowances:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = async (date) => {
    const dateStr = date.toISOString().split('T')[0];
    
    if (bulkEditMode) {
      // Handle bulk selection
      const newSelected = new Set(selectedDates);
      if (newSelected.has(dateStr)) {
        newSelected.delete(dateStr);
      } else {
        newSelected.add(dateStr);
      }
      setSelectedDates(newSelected);
    } else {
      // Handle single date edit
      const allowanceData = allowances[dateStr] || {
        date: dateStr,
        routeId: selectedRoute,
        dailyAllowance: 0,
        baseCurrency: 'USD',
        isLocked: false,
        exists: false
      };
      
      setSelectedDateData(allowanceData);
      setEditingDate(dateStr);
      setEditForm({
        dailyAllowance: allowanceData.dailyAllowance.toString(),
        baseCurrency: allowanceData.baseCurrency || 'USD',
        isLocked: allowanceData.isLocked,
        reason: ''
      });
      setShowEditModal(true);
    }
  };

  const handleSaveAllotment = async () => {
    try {
      const allowanceData = {
        dailyAllowance: parseFloat(editForm.dailyAllowance) || 0,
        baseCurrency: editForm.baseCurrency || 'USD',
        isLocked: editForm.isLocked,
        reason: editForm.reason || 'Manual update'
      };

      await allotmentService.setAllowance(
        selectedRoute,
        editingDate,
        allowanceData,
        'admin' // TODO: get from auth
      );

      setShowEditModal(false);
      fetchAllowances(); // Refresh data
      
      alert('Daily allowance updated successfully');
    } catch (error) {
      console.error('Error saving allowance:', error);
      alert('Error saving allowance: ' + error.message);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedDates.size === 0) {
      alert('Please select dates to update');
      return;
    }

    const allowanceValue = prompt('Enter daily allowance (USD) for selected dates:');
    const reason = prompt('Reason for bulk update:') || 'Bulk update';

    if (allowanceValue === null) return;

    try {
      // Update each selected date individually using the allowance service
      for (const date of selectedDates) {
        const allowanceData = {
          dailyAllowance: parseFloat(allowanceValue) || 0,
          baseCurrency: 'USD',
          reason
        };
        
        await allotmentService.setAllowance(selectedRoute, date, allowanceData, 'admin');
      }
      
      setSelectedDates(new Set());
      setBulkEditMode(false);
      fetchAllowances();
      
      alert(`Successfully updated ${selectedDates.size} dates`);
    } catch (error) {
      console.error('Error with bulk update:', error);
      alert('Error with bulk update: ' + error.message);
    }
  };

  // Version control functions
  const fetchVersions = async () => {
    if (!selectedRoute) return;
    
    try {
      setLoadingVersions(true);
      console.log('🔍 Fetching versions for route:', selectedRoute);
      const routeVersions = await calendarVersionService.getCalendarVersions(selectedRoute);
      console.log('📋 Fetched versions:', routeVersions);
      setVersions(routeVersions);
    } catch (error) {
      console.error('❌ Error fetching versions:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        selectedRoute
      });
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleSaveVersion = async () => {
    if (!selectedRoute || !versionForm.versionName.trim()) {
      alert('Please enter a version name');
      return;
    }

    try {
      // Get current month's date range
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

      await calendarVersionService.saveCalendarVersion(
        selectedRoute,
        startDate,
        endDate,
        versionForm.versionName,
        'admin', // TODO: get from auth
        versionForm.notes
      );

      setShowSaveVersionModal(false);
      setVersionForm({ versionName: '', notes: '' });
      fetchVersions();
      alert('Version saved successfully!');
    } catch (error) {
      console.error('Error saving version:', error);
      alert('Error saving version: ' + error.message);
    }
  };

  const handleLoadVersion = async (versionId) => {
    if (!window.confirm('This will replace current calendar data with the selected version. Continue?')) {
      return;
    }

    try {
      const result = await calendarVersionService.loadCalendarVersion(versionId, 'admin');
      setShowVersionModal(false);
      fetchAllowances(); // Refresh calendar
      alert(`Version loaded successfully! Applied ${result.appliedCount} of ${result.totalCount} dates.`);
    } catch (error) {
      console.error('Error loading version:', error);
      alert('Error loading version: ' + error.message);
    }
  };

  const handleSetDefaultVersion = async (versionId) => {
    try {
      await calendarVersionService.setDefaultVersion(selectedRoute, versionId, 'admin');
      fetchVersions(); // Refresh to show default indicator
      alert('Default version updated successfully!');
    } catch (error) {
      console.error('Error setting default version:', error);
      alert('Error setting default version: ' + error.message);
    }
  };

  // Load versions when route changes
  useEffect(() => {
    if (selectedRoute) {
      fetchVersions();
      
      // Run a quick test to check Firestore access
      calendarVersionService.testFirestoreAccess().then(result => {
        if (!result.success) {
          console.error('⚠️ Calendar versioning may not work due to Firestore access issues');
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute]);

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Get first day of month and calculate starting day of calendar
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay(); // 0 = Sunday
    
    const daysInMonth = lastDay.getDate();
    const calendarDays = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDay; i++) {
      calendarDays.push(null);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(day);
    }
    
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }
    
    return weeks.map((week, weekIndex) => (
      <tr key={weekIndex}>
        {week.map((day, dayIndex) => {
          if (day === null) {
            return <td key={dayIndex} className="calendar-empty"></td>;
          }
          
          const date = new Date(year, month, day);
          const dateStr = date.toISOString().split('T')[0];
          const allowanceData = allowances[dateStr] || { dailyAllowance: 0, baseCurrency: 'USD', isLocked: false };
          
          const isSelected = selectedDates.has(dateStr);
          const isToday = dateStr === new Date().toISOString().split('T')[0];
          const hasData = allowanceData.dailyAllowance > 0;
          const isLocked = allowanceData.isLocked;
          
          // Format allowance display with currency conversion
          let displayAmount = '';
          if (hasData) {
            if (displayCurrency === 'USD') {
              displayAmount = `$${allowanceData.dailyAllowance}`;
            } else {
              // For non-USD, we'd need to convert using currencyService
              displayAmount = `$${allowanceData.dailyAllowance}`;
            }
          }
          
          return (
            <td 
              key={dayIndex} 
              className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${hasData ? 'has-data' : ''} ${isLocked ? 'locked' : ''}`}
              onClick={() => handleDateClick(date)}
            >
              <div className="day-number">{day}</div>
              {hasData && (
                <div className="allowance-info">
                  <div className="allowance-amount">{displayAmount}</div>
                  {isLocked && <div className="lock-indicator">🔒</div>}
                </div>
              )}
            </td>
          );
        })}
      </tr>
    ));
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const selectedRouteName = routes.find(r => r.id === selectedRoute)?.name || '';

  if (loading && routes.length === 0) {
    return <div>Loading routes...</div>;
  }

  return (
    <div className="admin-pricing-calendar">
      <style>{`
        .admin-pricing-calendar {
          padding: 20px;
        }
        
        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .route-selector {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .route-selector select {
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .calendar-navigation {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .nav-button {
          background: #007bff;
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .nav-button:hover {
          background: #0056b3;
        }
        
        .month-year {
          font-size: 18px;
          font-weight: bold;
          color: #333;
        }
        
        .calendar-controls {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        
        .bulk-mode-toggle {
          background: #6c757d;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .bulk-mode-toggle.active {
          background: #28a745;
        }
        
        .bulk-update-button {
          background: #ffc107;
          color: #000;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
        }

        .version-save-button {
          background: #17a2b8;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .version-save-button:hover {
          background: #138496;
        }

        .version-load-button {
          background: #6f42c1;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .version-load-button:hover {
          background: #5a32a3;
        }
        
        .calendar-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
          border: 1px solid #ddd;
        }
        
        .calendar-table th {
          background: #f8f9fa;
          padding: 12px;
          text-align: center;
          border: 1px solid #ddd;
          font-weight: bold;
        }
        
        .calendar-day {
          width: 14.28%;
          height: 100px;
          border: 1px solid #ddd;
          padding: 5px;
          vertical-align: top;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .calendar-day:hover {
          background: #f0f0f0;
        }
        
        .calendar-day.today {
          background: #e3f2fd;
        }
        
        .calendar-day.selected {
          background: #bbdefb;
        }
        
        .calendar-day.has-data {
          background: #f0f8f0;
        }
        
        .calendar-day.locked {
          background: #fff3e0;
          border: 2px solid #ff9800;
        }
        
        .calendar-empty {
          width: 14.28%;
          height: 100px;
          border: 1px solid #ddd;
        }
        
        .day-number {
          font-weight: bold;
          font-size: 16px;
          color: #333;
        }
        
        .allowance-info {
          margin-top: 5px;
          font-size: 11px;
        }
        
        .allowance-amount {
          background: #28a745;
          color: white;
          padding: 2px 4px;
          border-radius: 3px;
          display: inline-block;
          margin-bottom: 2px;
          font-size: 10px;
        }
        
        .lock-indicator {
          font-size: 10px;
          text-align: center;
        }
        
        .edit-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        
        .edit-modal {
          background: white;
          padding: 30px;
          border-radius: 8px;
          width: 500px;
          max-width: 90vw;
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        
        .modal-title {
          font-size: 20px;
          font-weight: bold;
          color: #333;
        }
        
        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
        }
        
        .form-group {
          margin-bottom: 15px;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: bold;
          color: #333;
        }
        
        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          color: #333;
          background: white;
        }
        
        .form-group textarea {
          height: 60px;
          resize: vertical;
        }
        
        .checkbox-group {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .current-info {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 4px;
          margin-bottom: 20px;
        }
        
        .modal-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 20px;
        }
        
        .save-button {
          background: #28a745;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .cancel-button {
          background: #6c757d;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .legend {
          display: flex;
          gap: 20px;
          margin-top: 15px;
          font-size: 12px;
        }
        
        .legend-item {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        
        .legend-color {
          width: 15px;
          height: 15px;
          border: 1px solid #ccc;
        }
        
        @media (max-width: 768px) {
          .calendar-header {
            flex-direction: column;
            align-items: stretch;
          }
          
          .calendar-navigation {
            justify-content: center;
          }
          
          .calendar-controls {
            justify-content: center;
            flex-wrap: wrap;
          }
          
          .calendar-day {
            height: 80px;
            font-size: 12px;
          }
          
          .edit-modal {
            width: 95vw;
            padding: 20px;
          }
        }
      `}</style>

      <div className="calendar-header">
        <div className="route-selector">
          <label>Route:</label>
          <select 
            value={selectedRoute} 
            onChange={(e) => setSelectedRoute(e.target.value)}
          >
            {routes.map(route => (
              <option key={route.id} value={route.id}>
                {route.name}
              </option>
            ))}
          </select>
          
          <label style={{marginLeft: '20px'}}>Display Currency:</label>
          <select 
            value={displayCurrency} 
            onChange={(e) => setDisplayCurrency(e.target.value)}
          >
            {availableCurrencies.map(currency => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </div>

        <div className="calendar-navigation">
          <button 
            className="nav-button" 
            onClick={() => navigateMonth(-1)}
          >
            ← Previous
          </button>
          <div className="month-year">
            {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </div>
          <button 
            className="nav-button" 
            onClick={() => navigateMonth(1)}
          >
            Next →
          </button>
        </div>

        <div className="calendar-controls">
          <button 
            className={`bulk-mode-toggle ${bulkEditMode ? 'active' : ''}`}
            onClick={() => {
              setBulkEditMode(!bulkEditMode);
              setSelectedDates(new Set());
            }}
          >
            {bulkEditMode ? 'Exit Bulk Mode' : 'Bulk Edit Mode'}
          </button>
          
          {bulkEditMode && selectedDates.size > 0 && (
            <button 
              className="bulk-update-button"
              onClick={handleBulkUpdate}
            >
              Update {selectedDates.size} dates
            </button>
          )}

          <button 
            className="version-save-button"
            onClick={() => setShowSaveVersionModal(true)}
            title="Save current calendar as new version"
          >
            💾 Save Version
          </button>

          <button 
            className="version-load-button"
            onClick={() => setShowVersionModal(true)}
            title="Load a saved version"
          >
            📂 Load Version ({versions.length})
          </button>
        </div>
      </div>

      {loading ? (
        <div>Loading daily allowances...</div>
      ) : (
        <>
          <table className="calendar-table">
            <thead>
              <tr>
                <th>Sunday</th>
                <th>Monday</th>
                <th>Tuesday</th>
                <th>Wednesday</th>
                <th>Thursday</th>
                <th>Friday</th>
                <th>Saturday</th>
              </tr>
            </thead>
            <tbody>
              {renderCalendar()}
            </tbody>
          </table>

          <div className="legend">
            <div className="legend-item">
              <div className="legend-color" style={{background: '#e3f2fd'}}></div>
              <span>Today</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{background: '#f0f8f0'}}></div>
              <span>Has Daily Allowance</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{background: '#fff3e0', border: '2px solid #ff9800'}}></div>
              <span>Locked</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{background: '#bbdefb'}}></div>
              <span>Selected</span>
            </div>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <div className="modal-header">
              <div className="modal-title">
                Edit Daily Allowance - {editingDate} ({selectedRouteName})
              </div>
              <button 
                className="close-button" 
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>

            {selectedDateData && (
              <div className="current-info">
                <strong>Current Daily Allowance:</strong><br/>
                Amount: ${selectedDateData.dailyAllowance || 0} USD<br/>
                Currency: {selectedDateData.baseCurrency || 'USD'}<br/>
                Status: {selectedDateData.isLocked ? 'Locked' : 'Unlocked'}
              </div>
            )}

            <div className="form-group">
              <label>Daily Allowance (USD):</label>
              <input
                type="number"
                step="0.01"
                value={editForm.dailyAllowance}
                onChange={(e) => setEditForm({...editForm, dailyAllowance: e.target.value})}
                placeholder="Enter daily allowance in USD"
                min="0"
              />
              <small style={{color: '#666', fontSize: '12px', display: 'block', marginTop: '5px'}}>
                This is the amount BackPass will cover per day for room bookings
              </small>
            </div>

            <div className="form-group">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="isLocked"
                  checked={editForm.isLocked}
                  onChange={(e) => setEditForm({...editForm, isLocked: e.target.checked})}
                />
                <label htmlFor="isLocked">Lock this date (prevent automatic changes)</label>
              </div>
            </div>

            <div className="form-group">
              <label>Reason for change:</label>
              <textarea
                value={editForm.reason}
                onChange={(e) => setEditForm({...editForm, reason: e.target.value})}
                placeholder="Enter reason for this change..."
              />
            </div>

            <div className="modal-actions">
              <button 
                className="cancel-button" 
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-button" 
                onClick={handleSaveAllotment}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Version Modal */}
      {showSaveVersionModal && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <div className="modal-header">
              <div className="modal-title">
                Save Current Calendar as Version
              </div>
              <button 
                className="close-button" 
                onClick={() => setShowSaveVersionModal(false)}
              >
                ×
              </button>
            </div>

            <div className="form-group">
              <label>Version Name:</label>
              <input
                type="text"
                value={versionForm.versionName}
                onChange={(e) => setVersionForm({...versionForm, versionName: e.target.value})}
                placeholder="e.g., August 2025 Final, Summer Season v2"
                required
              />
            </div>

            <div className="form-group">
              <label>Notes (Optional):</label>
              <textarea
                value={versionForm.notes}
                onChange={(e) => setVersionForm({...versionForm, notes: e.target.value})}
                placeholder="Describe the changes or purpose of this version..."
                rows={3}
              />
            </div>

            <div className="current-info">
              <strong>What will be saved:</strong><br/>
              Current month: {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}<br/>
              Route: {routes.find(r => r.id === selectedRoute)?.name}<br/>
              All daily allowances for this month
            </div>

            <div className="modal-actions">
              <button 
                className="cancel-button" 
                onClick={() => setShowSaveVersionModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-button" 
                onClick={handleSaveVersion}
                disabled={!versionForm.versionName.trim()}
              >
                Save Version
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Version Modal */}
      {showVersionModal && (
        <div className="edit-modal-overlay">
          <div className="edit-modal" style={{maxWidth: '600px', width: '90vw'}}>
            <div className="modal-header">
              <div className="modal-title">
                Load Calendar Version
              </div>
              <button 
                className="close-button" 
                onClick={() => setShowVersionModal(false)}
              >
                ×
              </button>
            </div>

            {loadingVersions ? (
              <div style={{padding: '20px', textAlign: 'center'}}>Loading versions...</div>
            ) : versions.length === 0 ? (
              <div style={{padding: '20px', textAlign: 'center', color: '#666'}}>
                No saved versions found for this route.
              </div>
            ) : (
              <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                {versions.map(version => (
                  <div key={version.id} style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '15px',
                    marginBottom: '10px',
                    backgroundColor: version.isDefault ? '#f0f8f0' : 'white'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                      <div style={{flex: 1}}>
                        <div style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '5px'}}>
                          {version.versionName}
                          {version.isDefault && (
                            <span style={{
                              background: '#28a745',
                              color: 'white',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              marginLeft: '8px'
                            }}>
                              DEFAULT
                            </span>
                          )}
                        </div>
                        <div style={{fontSize: '12px', color: '#666', marginBottom: '5px'}}>
                          Created: {version.createdAt instanceof Date 
                            ? version.createdAt.toLocaleDateString() 
                            : new Date(version.createdAt).toLocaleDateString()}
                          <br/>
                          By: {version.createdBy} | {version.allowanceCount} dates
                        </div>
                        {version.notes && (
                          <div style={{fontSize: '13px', color: '#333', fontStyle: 'italic'}}>
                            "{version.notes}"
                          </div>
                        )}
                      </div>
                      <div style={{display: 'flex', gap: '5px', flexDirection: 'column'}}>
                        <button
                          onClick={() => handleLoadVersion(version.id)}
                          style={{
                            background: '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Load
                        </button>
                        {!version.isDefault && (
                          <button
                            onClick={() => handleSetDefaultVersion(version.id)}
                            style={{
                              background: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '5px 10px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Set Default
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="modal-actions">
              <button 
                className="cancel-button" 
                onClick={() => setShowVersionModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPricingCalendar;